import { type CSSProperties } from "react";

/**
 * BESPOKE UI ICON SET — hand-drawn, COLOURED SVG chrome icons that replace the emoji across the UI. One
 * coherent style: a 24×24 viewBox, flat filled shapes in the game's cozy garden palette (greens, wood browns,
 * water blues, gold coin…) with a soft dark outline so each icon pops on the dark menu bar AND on the green
 * "active" button. Use `<Icon name="plant" />` anywhere an emoji glyph used to be — it sizes to 1em so it
 * matches the surrounding `.mi` font-size + HUD scale.
 *
 * This is CHROME iconography (menus, buttons, HUD) — distinct from the procedural game ART (PlantIcon,
 * DecorIcon, FishArt, InsectArt) which draws the actual plants/creatures. Don't put those here.
 *
 * Adding an icon: draw it in the 24×24 box with fills from C (below) + the shared `.stroke` outline colour.
 * Keep the palette + visual weight consistent with the neighbours.
 */

/** Shared palette — kept small + reused so the whole set reads as one family. */
const C = {
  leaf: "#7bc24a", leafDk: "#4f9a34", stem: "#4f9a34",
  wood: "#b07a43", woodDk: "#8a5c30",
  water: "#5bb7ea", waterDk: "#3a8fca",
  soil: "#8a5a3b", soilDk: "#6b4329",
  gold: "#f2c14e", goldDk: "#d19a2e",
  petalP: "#ef7fb0", petalPk: "#d64f8f",
  petalY: "#ffd24a",
  metal: "#c3ccd6", metalDk: "#8b97a5",
  glass: "#bfe4f5", stone: "#b9c0c8",
  red: "#e8794b", ink: "#2a2e34",
  paper: "#f2ede0",
};
const OUT = "#2a2e34";           // shared outline (soft ink)
const OUT_W = 1.4;               // outline stroke width

/** Shared SVG wrapper. Fills come from the paths; a thin outline is applied per-shape where set. */
function Svg({ children, size, style, className }: { children: React.ReactNode; size?: number; style?: CSSProperties; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size ?? "1em"}
      height={size ?? "1em"}
      stroke={OUT}
      strokeWidth={OUT_W}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block", overflow: "visible", ...style }}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export type IconName =
  // screen switcher
  | "greenhouse" | "garden" | "lock"
  // greenhouse tools
  | "plant" | "harvest" | "water"
  // garden tools (grouped)
  | "grow" | "build" | "expand" | "fence" | "arrange" | "move" | "pickup" | "terrain"
  // fishing / bugs
  | "fish" | "net"
  // markets + shared
  | "fish-market" | "bug-market" | "market" | "shop" | "inventory" | "guide" | "dev" | "exit" | "menu"
  // misc chrome
  | "chevron-down" | "chevron-left" | "chevron-right" | "coin"
  // phase 2 — common modal/HUD chrome
  | "close" | "star" | "sparkle" | "up" | "down" | "plus" | "minus" | "check" | "pending"
  | "warning" | "trophy" | "seed" | "bouquet" | "moneybag" | "recycle" | "bulb" | "timer"
  | "sun" | "plug" | "power" | "compost" | "gear" | "ruler" | "chart"
  | "trash" | "flower" | "leaf" | "crystal" | "sign" | "target" | "dice" | "microscope"
  | "backdrop" | "sliders" | "wave" | "speed" | "clock" | "eye" | "save" | "cloud" | "sync"
  | "calendar" | "book" | "puzzle" | "mouse" | "wheat" | "droplet-small" | "note" | "bottle" | "camera"
  // phase 3 — weather (8) + time of day (6)
  | "wx-bright" | "wx-clear" | "wx-fog" | "wx-partly" | "wx-mostly" | "wx-overcast" | "wx-lightrain" | "wx-heavyrain" | "wx-storm"
  | "tod-dawn" | "tod-sunrise" | "tod-day" | "tod-sunset" | "tod-dusk" | "tod-night"

/** The icon registry — coloured, flat, 24×24. */
const PATHS: Record<IconName, React.ReactNode> = {
  // ── screen switcher ──
  greenhouse: (<>
    <path d="M12 3 3.5 8v11.5h17V8z" fill={C.glass} />
    <path d="M12 3 3.5 8v11.5h17V8z" fill="none" />
    <path d="M12 3v16.5M4 8h16M7.5 13h9" stroke={C.waterDk} strokeWidth={1.2} fill="none" />
    <path d="M12 3 3.5 8h17z" fill={C.leafDk} stroke="none" opacity=".5" />
  </>),
  garden: (<>
    <path d="M12 20v2" stroke={C.stem} strokeWidth={2} />
    <path d="M12 4a5 5 0 0 1 4.6 7A4.2 4.2 0 0 1 15 19.4H9A4.2 4.2 0 0 1 7.4 11 5 5 0 0 1 12 4Z" fill={C.leaf} />
    <path d="M12 8a4 4 0 0 0-3 6" stroke={C.leafDk} strokeWidth={1.2} fill="none" opacity=".7" />
  </>),
  lock: (<>
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" fill="none" stroke={C.metalDk} strokeWidth={2} />
    <rect x="5" y="10.5" width="14" height="10" rx="2.4" fill={C.gold} />
    <circle cx="12" cy="15" r="1.4" fill={C.goldDk} stroke="none" />
    <path d="M12 15.8V17.6" stroke={C.goldDk} strokeWidth={1.6} />
  </>),

  // ── greenhouse tools ──
  plant: (<>
    <path d="M12 20v-8" stroke={C.stem} strokeWidth={2} fill="none" />
    <path d="M12 12.5c0-3.2 2.7-4.8 5.2-4.8C17.2 10.9 14.9 12.5 12 12.5Z" fill={C.leaf} />
    <path d="M12 14c0-2.7-2.5-4.2-5.2-4.2C6.8 12.9 9.1 14.5 12 14Z" fill={C.leafDk} />
    <path d="M6.5 20h11" stroke={C.soilDk} strokeWidth={2} fill="none" />
  </>),
  harvest: (<>
    <path d="M8.7 15.4 20 5M8.7 8.6 20 19" stroke={C.metalDk} strokeWidth={2} fill="none" />
    <circle cx="6.5" cy="17" r="2.6" fill={C.red} />
    <circle cx="6.5" cy="7" r="2.6" fill={C.red} />
    <path d="M18 4l2.5 1-1 2.5M18 20l2.5-1-1-2.5" stroke={C.metalDk} strokeWidth={1.4} fill="none" />
  </>),
  water: (<>
    <path d="M12 3.2c3.1 3.9 5.2 6.7 5.2 9.6A5.2 5.2 0 0 1 6.8 12.8c0-2.9 2.1-5.7 5.2-9.6Z" fill={C.water} />
    <path d="M9.3 13.4a2.6 2.6 0 0 0 2 2.4" stroke={C.glass} strokeWidth={1.4} fill="none" />
  </>),

  // ── garden tools ──
  grow: (<>
    <path d="M12 13v-3.5" stroke={C.stem} strokeWidth={1.8} fill="none" />
    <path d="M12 10c0-2.2 1.9-3.3 3.7-3.3C15.7 8.9 13.9 10 12 10Z" fill={C.leaf} />
    <path d="M7.5 13h9l-1 6.4a1.6 1.6 0 0 1-1.6 1.4h-2.8A1.6 1.6 0 0 1 8.5 19.4Z" fill={C.wood} />
    <path d="M7.5 13h9l-.3 2h-8.4z" fill={C.woodDk} stroke="none" />
  </>),
  build: (<>
    <path d="M14.6 6.4 18 3l3 3-3.4 3.4-1-1-1.9 1.9 1 1L8 19a2 2 0 0 1-3-3l7.3-7.3 1 1 1.9-1.9-1-1z" fill={C.metal} />
    <path d="M18 3l3 3-1.3 1.3-3-3z" fill={C.woodDk} stroke="none" />
  </>),
  expand: (<>
    <rect x="3.5" y="3.5" width="17" height="17" rx="3" fill="none" stroke={C.leafDk} strokeWidth={1.4} strokeDasharray="2.6 2.6" />
    <circle cx="12" cy="12" r="5.5" fill={C.leaf} />
    <path d="M12 9v6M9 12h6" stroke="#fff" strokeWidth={2} fill="none" />
  </>),
  terrain: (<>
    {/* a little paved path winding up a sandy ground — flagstones + a sandy border */}
    <rect x="3" y="4" width="18" height="16" rx="3" fill="#d9c188" />
    <path d="M9 20l0.5-4 3-1.5 0.3-4 2.5-1.6" fill="none" stroke={C.stone} strokeWidth={4.4} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 20l0.5-4 3-1.5 0.3-4 2.5-1.6" fill="none" stroke="#7f858c" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="0.5 3" />
    <circle cx="6.5" cy="8" r="0.9" fill="#b6a06a" />
    <circle cx="17.5" cy="16" r="0.9" fill="#b6a06a" />
  </>),
  fence: (<>
    <path d="M5.5 20V9l1.5-2L8.5 9v11z" fill={C.wood} />
    <path d="M14.5 20V9l1.5-2L17.5 9v11z" fill={C.wood} />
    <path d="M3.5 11.5h17M3.5 15.5h17" stroke={C.woodDk} strokeWidth={1.8} fill="none" />
  </>),
  arrange: (<>
    <path d="M8 12v-6a1.4 1.4 0 0 1 2.8 0M10.8 11V5a1.4 1.4 0 0 1 2.8 0v5M13.6 10.5V6.5a1.4 1.4 0 0 1 2.8 0" fill="none" stroke={C.soilDk} strokeWidth={1.8} />
    <path d="M8 12v2.2l-1.5-1.6a1.4 1.4 0 0 0-2 2L7 20a4 4 0 0 0 3 1.5h3a4 4 0 0 0 4-4V6.5a1.4 1.4 0 0 0-2.8 0" fill="#f2c99a" />
  </>),
  move: (<>
    <path d="M12 3 9.4 6h5.2zM12 21l-2.6-3h5.2zM3 12l3-2.6v5.2zM21 12l-3-2.6v5.2z" fill={C.leaf} />
    <path d="M12 6v12M6 12h12" stroke={C.leafDk} strokeWidth={1.8} fill="none" />
  </>),
  pickup: (<>
    <circle cx="12" cy="5" r="2" fill={C.leaf} />
    <path d="M7 12c1-1.6 2.5-2.4 5-2.4s4 .8 5 2.4v3a5 5 0 0 1-10 0z" fill="#f2c99a" />
    <path d="M7.5 12.5c1-1.2 2.5-1.9 4.5-1.9s3.5.7 4.5 1.9" stroke={C.soilDk} strokeWidth={1.2} fill="none" />
  </>),

  // ── fishing / bugs ──
  fish: (<>
    <path d="M5.5 20 17 5" stroke={C.wood} strokeWidth={2.2} fill="none" />
    <path d="M17 5l1.6-1.6" stroke={C.woodDk} strokeWidth={1.6} fill="none" />
    <path d="M17 5v6a2.2 2.2 0 0 1-2.2 2.2A1.7 1.7 0 0 1 14.8 10" stroke={C.metalDk} strokeWidth={1.4} fill="none" />
    <circle cx="6" cy="18.5" r="1.6" fill={C.red} />
  </>),
  net: (<>
    <path d="M12.6 12.6 20 20" stroke={C.wood} strokeWidth={2.2} fill="none" />
    <circle cx="9" cy="9" r="5.4" fill={C.glass} opacity=".85" />
    <circle cx="9" cy="9" r="5.4" fill="none" stroke={C.metalDk} strokeWidth={1.6} />
    <path d="M6 7.5h6M5 10.5h8M8 5.2v8M11 5.6v8" stroke={C.metalDk} strokeWidth={0.9} fill="none" opacity=".6" />
  </>),

  // ── markets + shared ──
  "fish-market": (<>
    <path d="M4 12c3-4 8-5 12-3-1.4 1.3-1.4 4.7 0 6-4 2-9 1-12-3Z" fill={C.water} />
    <path d="M16 9c1.6-1.2 3.5-1.4 5-.7-1 1.1-1 3.3 0 4.4-1.5.7-3.4.5-5-.7" fill={C.waterDk} />
    <circle cx="8" cy="11" r="1.1" fill="#fff" /><circle cx="8" cy="11" r=".5" fill={C.ink} stroke="none" />
  </>),
  "bug-market": (<>
    <path d="M12 5.5 10.6 3.8M12 5.5 13.4 3.8" stroke={C.ink} strokeWidth={1.3} fill="none" />
    <path d="M12 8C10.4 4.9 4 4.4 4.5 9c.4 3.6 4.6 4.6 7.5 3Z" fill={C.petalP} />
    <path d="M12 8c1.6-3.1 8-3.6 7.5 1-.4 3.6-4.6 4.6-7.5 3Z" fill={C.petalPk} />
    <path d="M12 6.5v12" stroke={C.ink} strokeWidth={1.6} fill="none" />
    <circle cx="7.5" cy="8.5" r="1" fill="#fff" stroke="none" opacity=".7" /><circle cx="16.5" cy="8.5" r="1" fill="#fff" stroke="none" opacity=".7" />
  </>),
  market: (<>
    <path d="M4 4v16h16" stroke={C.metalDk} strokeWidth={2} fill="none" />
    <path d="M7 15l3.5-3.5L13 14l4.5-5" stroke={C.leafDk} strokeWidth={2.2} fill="none" />
    <path d="M17.5 9H14M17.5 9v3.5" stroke={C.leafDk} strokeWidth={1.8} fill="none" />
    <circle cx="17.5" cy="9" r="1.4" fill={C.leaf} stroke="none" />
  </>),
  shop: (<>
    <path d="M6 8h12l-1 11.6a1.6 1.6 0 0 1-1.6 1.4H8.6A1.6 1.6 0 0 1 7 19.6Z" fill={C.petalP} />
    <path d="M6 8h12l-.3 3H6.3z" fill={C.petalPk} stroke="none" />
    <path d="M9 8V6.5a3 3 0 0 1 6 0V8" fill="none" stroke={C.petalPk} strokeWidth={2} />
  </>),
  inventory: (<>
    <path d="M6 10a6 6 0 0 1 12 0v8.6a1.6 1.6 0 0 1-1.6 1.4H7.6A1.6 1.6 0 0 1 6 18.6Z" fill={C.wood} />
    <path d="M9 8.5a3 3 0 0 1 6 0" fill="none" stroke={C.woodDk} strokeWidth={1.8} />
    <rect x="9" y="12.5" width="6" height="4.6" rx="1.2" fill={C.woodDk} />
  </>),
  guide: (<>
    <path d="M12 6c-2-1.4-4.5-1.8-7-1.3v12.6c2.5-.5 5-.1 7 1.3z" fill={C.paper} />
    <path d="M12 6c2-1.4 4.5-1.8 7-1.3v12.6c-2.5-.5-5-.1-7 1.3z" fill="#e6ddc8" />
    <path d="M12 6v13" stroke={C.woodDk} strokeWidth={1.4} fill="none" />
    <path d="M6.5 8h3M6.5 10.5h3M14.5 8h3M14.5 10.5h3" stroke={C.woodDk} strokeWidth={1} fill="none" opacity=".6" />
  </>),
  dev: (<>
    <path d="M14.6 6.4a3.6 3.6 0 0 0 4.6 4.6L21 13l-8 8-2-2 1.9-1.9a3.6 3.6 0 0 1-4.6-4.6z" fill={C.metal} />
    <path d="m6 6 3.2 3.2" stroke={C.woodDk} strokeWidth={1.8} fill="none" />
    <circle cx="5" cy="5" r="1.6" fill={C.red} />
  </>),
  exit: (<>
    <path d="M13 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" fill={C.wood} />
    <path d="M13 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" fill="none" stroke={C.woodDk} strokeWidth={1.5} />
    <circle cx="10.5" cy="12" r=".9" fill={C.gold} stroke="none" />
    <path d="M11 12h9M17 9l3 3-3 3" stroke={C.red} strokeWidth={2} fill="none" />
  </>),
  menu: (<>
    <path d="M4 7h16M4 12h16M4 17h16" stroke={C.woodDk} strokeWidth={2.2} fill="none" strokeLinecap="round" />
  </>),

  // ── misc chrome ──
  "chevron-down": (<path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth={2} />),
  "chevron-left": (<path d="m15 6-6 6 6 6" fill="none" stroke="currentColor" strokeWidth={2} />),
  "chevron-right": (<path d="m9 6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth={2} />),
  coin: (<>
    <circle cx="12" cy="12" r="8.4" fill={C.gold} />
    <circle cx="12" cy="12" r="5.2" fill="none" stroke={C.goldDk} strokeWidth={1.3} />
    <path d="M12 8.5v7M10 10.5h3a1.5 1.5 0 0 1 0 3h-2.5" stroke={C.goldDk} strokeWidth={1.3} fill="none" />
  </>),

  // ── phase 2 — common modal/HUD chrome ──
  close: (<path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth={2} />),
  star: (<path d="M12 3.5l2.4 5 5.5.7-4 3.8 1 5.4-4.9-2.7-4.9 2.7 1-5.4-4-3.8 5.5-.7z" fill={C.gold} />),
  sparkle: (<>
    <path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6z" fill={C.petalY} />
    <path d="M18 15l.7 2.3L21 18l-2.3.7L18 21l-.7-2.3L15 18l2.3-.7z" fill={C.gold} />
  </>),
  up: (<path d="M12 5v14M6 11l6-6 6 6" fill="none" stroke={C.leafDk} strokeWidth={2} />),
  down: (<path d="M12 5v14M6 13l6 6 6-6" fill="none" stroke={C.red} strokeWidth={2} />),
  plus: (<path d="M12 5v14M5 12h14" fill="none" stroke={C.leafDk} strokeWidth={2.2} />),
  minus: (<path d="M5 12h14" fill="none" stroke={C.red} strokeWidth={2.2} />),
  check: (<path d="M5 12.5 10 17.5 19 6.5" fill="none" stroke={C.leaf} strokeWidth={2.4} />),
  pending: (<>
    <circle cx="12" cy="12" r="8.2" fill="none" stroke={C.goldDk} strokeWidth={1.8} />
    <path d="M12 7.5v4.5l3 2" fill="none" stroke={C.goldDk} strokeWidth={1.8} />
  </>),
  warning: (<>
    <path d="M12 4 21 19H3z" fill={C.gold} />
    <path d="M12 10v4.5" stroke={C.ink} strokeWidth={2} fill="none" /><circle cx="12" cy="17" r="1.1" fill={C.ink} stroke="none" />
  </>),
  trophy: (<>
    <path d="M8 4h8v4a4 4 0 0 1-8 0z" fill={C.gold} />
    <path d="M8 5H5v1.5A2.5 2.5 0 0 0 7.7 9M16 5h3v1.5A2.5 2.5 0 0 1 16.3 9" fill="none" stroke={C.goldDk} strokeWidth={1.5} />
    <path d="M12 12v3M9 20h6M10 20l.5-5h3l.5 5" fill={C.gold} />
  </>),
  seed: (<>
    <ellipse cx="12" cy="13" rx="5" ry="7" fill={C.soil} transform="rotate(20 12 13)" />
    <path d="M10 9c1.5 1 3 3.5 3.5 6" stroke={C.soilDk} strokeWidth={1.2} fill="none" />
  </>),
  bouquet: (<>
    <path d="M12 21 8 13M12 21l4-8" stroke={C.stem} strokeWidth={1.8} fill="none" />
    <circle cx="8" cy="9" r="3" fill={C.petalP} /><circle cx="16" cy="9" r="3" fill={C.petalY} /><circle cx="12" cy="6.5" r="3" fill={C.red} />
    <path d="M9 21h6" stroke={C.wood} strokeWidth={2} fill="none" />
  </>),
  moneybag: (<>
    <path d="M9 6h6l1.5 2c2 1.5 3 4 3 6.5A5.5 5.5 0 0 1 12 20a5.5 5.5 0 0 1-7.5-5.5c0-2.5 1-5 3-6.5z" fill={C.soil} />
    <circle cx="12" cy="14" r="4.5" fill={C.gold} /><path d="M12 11.5v5M10.5 12.8h2a1.2 1.2 0 0 1 0 2.4h-1.8" stroke={C.goldDk} strokeWidth={1.1} fill="none" />
    <path d="M9 6h6l-1 2h-4z" fill={C.soilDk} stroke="none" />
  </>),
  recycle: (<path d="M8 8 6 11l-2.5-.6M11 5.5 12.5 3 15 5m1 3 2 3 2.5-.6M13 18.5 11.5 21 9 19M6 11a6 6 0 0 1 5-3.5M18 14a6 6 0 0 1-5 4.5" fill="none" stroke={C.leaf} strokeWidth={1.8} />),
  bulb: (<>
    <path d="M12 3a6 6 0 0 1 3.5 10.8c-.7.6-1 1.2-1 2.2H9.5c0-1-.3-1.6-1-2.2A6 6 0 0 1 12 3Z" fill={C.petalY} />
    <path d="M9.5 17.5h5M10 20h4" stroke={C.metalDk} strokeWidth={1.6} fill="none" />
  </>),
  timer: (<>
    <path d="M9 3h6" stroke={C.metalDk} strokeWidth={2} fill="none" />
    <circle cx="12" cy="14" r="7" fill={C.glass} /><path d="M12 14V9.5" stroke={C.waterDk} strokeWidth={1.8} fill="none" />
  </>),
  sun: (<>
    <circle cx="12" cy="12" r="5" fill={C.gold} />
    <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M5 5l1.8 1.8M17.2 17.2 19 19M19 5l-1.8 1.8M6.8 17.2 5 19" stroke={C.goldDk} strokeWidth={1.8} fill="none" />
  </>),
  plug: (<>
    <path d="M9 3v5M15 3v5" stroke={C.metalDk} strokeWidth={2} fill="none" />
    <path d="M7 8h10v2a5 5 0 0 1-10 0z" fill={C.metal} /><path d="M12 15v6" stroke={C.metalDk} strokeWidth={2} fill="none" />
  </>),
  power: (<path d="M13 3 5 13h6l-1 8 8-10h-6z" fill={C.petalY} stroke={C.goldDk} strokeWidth={1.2} />),
  compost: (<>
    <path d="M6 10h12l-1 8.5a1.6 1.6 0 0 1-1.6 1.5H8.6A1.6 1.6 0 0 1 7 18.5z" fill={C.wood} />
    <path d="M5 8h14v2H5z" fill={C.woodDk} />
    <path d="M12 13c0-1.5 1.4-2.4 2.8-2.4C14.8 12.2 13.4 13 12 13Z" fill={C.leaf} stroke="none" />
    <path d="M12 13.5c0-1.5-1.4-2.4-2.8-2.4C9.2 12.7 10.6 13.5 12 13.5Z" fill={C.leafDk} stroke="none" />
  </>),
  gear: (<>
    <path d="M12 2.5l1.4 2.2 2.6-.5.4 2.6 2.4 1.1-1 2.4 1.6 2.1-2.1 1.6.3 2.6-2.6.3-1.3 2.3-2.4-1-2.4 1-1.3-2.3-2.6-.3.3-2.6L2 15l1.6-2.1-1-2.4L5 9.4l.4-2.6 2.6.5z" fill={C.metal} />
    <circle cx="12" cy="12" r="3" fill={C.glass} />
  </>),
  ruler: (<>
    <rect x="3" y="9" width="18" height="6" rx="1" fill={C.gold} transform="rotate(-20 12 12)" />
    <path d="M7 8.5l1 2M10.5 6.8l1.4 2.6M14 5l1 2" stroke={C.goldDk} strokeWidth={1.2} fill="none" transform="rotate(-20 12 12)" />
  </>),
  chart: (<>
    <rect x="4" y="12" width="4" height="8" rx="1" fill={C.leaf} /><rect x="10" y="8" width="4" height="12" rx="1" fill={C.water} /><rect x="16" y="4" width="4" height="16" rx="1" fill={C.petalP} />
  </>),
  trash: (<>
    <path d="M5 7h14l-1 12.5a1.6 1.6 0 0 1-1.6 1.5H7.6A1.6 1.6 0 0 1 6 19.5z" fill={C.metal} />
    <path d="M4 7h16M9 7V5h6v2M10 11v6M14 11v6" fill="none" stroke={C.metalDk} strokeWidth={1.5} />
  </>),
  flower: (<>
    <circle cx="12" cy="7" r="2.6" fill={C.petalP} /><circle cx="7.5" cy="10.5" r="2.6" fill={C.petalP} /><circle cx="16.5" cy="10.5" r="2.6" fill={C.petalP} /><circle cx="9.5" cy="15" r="2.6" fill={C.petalP} /><circle cx="14.5" cy="15" r="2.6" fill={C.petalP} />
    <circle cx="12" cy="11.5" r="2.4" fill={C.petalY} stroke="none" />
  </>),
  leaf: (<>
    <path d="M5 19C5 11 11 5 19 5c0 8-6 14-14 14Z" fill={C.leaf} />
    <path d="M6 18C9 13 13 9 18 6" stroke={C.leafDk} strokeWidth={1.3} fill="none" />
  </>),
  crystal: (<>
    <path d="M12 3 18 9l-6 12L6 9z" fill={C.water} />
    <path d="M6 9h12M12 3v18M9 9l3 12 3-12" stroke={C.glass} strokeWidth={1} fill="none" opacity=".7" />
  </>),
  sign: (<>
    <path d="M11 20V7" stroke={C.woodDk} strokeWidth={2} fill="none" />
    <rect x="4" y="6" width="14" height="7" rx="1.2" fill={C.wood} /><path d="M5 8.5h9M5 10.5h6" stroke={C.woodDk} strokeWidth={1} fill="none" opacity=".6" />
  </>),
  target: (<>
    <circle cx="12" cy="12" r="8.4" fill="#fff" /><circle cx="12" cy="12" r="8.4" fill="none" stroke={C.red} /><circle cx="12" cy="12" r="5" fill="none" stroke={C.red} strokeWidth={1.4} /><circle cx="12" cy="12" r="1.8" fill={C.red} stroke="none" />
  </>),
  dice: (<>
    <rect x="4.5" y="4.5" width="15" height="15" rx="3" fill={C.paper} />
    <circle cx="9" cy="9" r="1.3" fill={C.ink} stroke="none" /><circle cx="15" cy="9" r="1.3" fill={C.ink} stroke="none" /><circle cx="12" cy="12" r="1.3" fill={C.ink} stroke="none" /><circle cx="9" cy="15" r="1.3" fill={C.ink} stroke="none" /><circle cx="15" cy="15" r="1.3" fill={C.ink} stroke="none" />
  </>),
  microscope: (<>
    <path d="M6 20h13" stroke={C.metalDk} strokeWidth={2} fill="none" />
    <path d="M11 20a6 6 0 0 0 5-9" fill="none" stroke={C.metalDk} strokeWidth={1.8} />
    <path d="M14 4l3 2-5 8-3-2z" fill={C.water} /><path d="M8.5 12l3 2" stroke={C.metalDk} strokeWidth={2} fill="none" />
  </>),
  backdrop: (<>
    <rect x="3.5" y="4.5" width="17" height="13" rx="2" fill={C.glass} />
    <path d="M4 14l4-4 3 3 3.5-3.5L20 13" stroke={C.leafDk} strokeWidth={1.6} fill="none" /><circle cx="8" cy="8" r="1.4" fill={C.gold} stroke="none" />
  </>),
  sliders: (<>
    <path d="M6 4v16M12 4v16M18 4v16" stroke={C.metalDk} strokeWidth={1.6} fill="none" />
    <circle cx="6" cy="9" r="2.2" fill={C.leaf} /><circle cx="12" cy="15" r="2.2" fill={C.water} /><circle cx="18" cy="7" r="2.2" fill={C.petalP} />
  </>),
  wave: (<path d="M3 9c2-2 4-2 6 0s4 2 6 0 4-2 6 0M3 15c2-2 4-2 6 0s4 2 6 0 4-2 6 0" fill="none" stroke={C.water} strokeWidth={2} />),
  speed: (<>
    <path d="M6 20 5 22M18 20l1 2" stroke={C.metalDk} strokeWidth={1.4} fill="none" />
    <path d="M15 4l-3 8 5-4z" fill={C.red} /><path d="M4 20a8 8 0 1 1 16 0z" fill={C.glass} /><path d="M4 20a8 8 0 1 1 16 0" fill="none" stroke={C.metalDk} strokeWidth={1.6} />
  </>),
  clock: (<>
    <circle cx="12" cy="12" r="8.2" fill={C.glass} /><path d="M12 7.5V12l3 2" stroke={C.waterDk} strokeWidth={1.8} fill="none" />
  </>),
  eye: (<>
    <path d="M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" fill={C.glass} />
    <circle cx="12" cy="12" r="3" fill={C.waterDk} stroke="none" /><circle cx="12" cy="12" r="1.2" fill={C.ink} stroke="none" />
  </>),
  save: (<>
    <path d="M5 4h11l3 3v12a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V5.5A1.5 1.5 0 0 1 5.5 4Z" fill={C.water} />
    <rect x="8" y="4" width="6" height="4" rx=".6" fill={C.glass} stroke="none" /><rect x="7.5" y="12" width="9" height="5.5" rx=".8" fill={C.glass} stroke="none" />
  </>),
  camera: (<>
    <path d="M3.5 8.5A1.5 1.5 0 0 1 5 7h2.5l1.5-2.5h6L16.5 7H19a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5Z" fill={C.metal} />
    <circle cx="12" cy="13" r="3.8" fill={C.glass} /><circle cx="12" cy="13" r="1.6" fill={C.waterDk} stroke="none" />
  </>),
  cloud: (<path d="M7 18a4 4 0 0 1-.5-8A5 5 0 0 1 16 9.5a3.5 3.5 0 0 1 1 6.9z" fill={C.glass} />),
  sync: (<path d="M19 8a8 8 0 0 0-14-2M5 6V3M5 6h3M5 16a8 8 0 0 0 14 2M19 18v3M19 18h-3" fill="none" stroke={C.water} strokeWidth={1.8} />),
  calendar: (<>
    <rect x="4" y="5" width="16" height="15" rx="2" fill={C.paper} /><path d="M4 9h16" stroke={C.woodDk} strokeWidth={1.5} fill="none" /><path d="M8 3v3M16 3v3" stroke={C.woodDk} strokeWidth={2} fill="none" />
    <rect x="7" y="12" width="3" height="3" rx=".5" fill={C.red} stroke="none" />
  </>),
  book: (<>
    <path d="M6 4h11a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" fill={C.petalP} />
    <path d="M8 4v16" stroke="#fff" strokeWidth={1.2} fill="none" opacity=".5" />
  </>),
  puzzle: (<path d="M9 4a1.5 1.5 0 0 1 3 0c0 .8.7 1 1.5 1H16v2.5c0 .8.2 1.5 1 1.5a1.5 1.5 0 0 1 0 3c-.8 0-1 .7-1 1.5V16h-2.5c-.8 0-1.5.2-1.5 1a1.5 1.5 0 0 1-3 0c0-.8-.7-1-1.5-1H4v-2.5c0-.8-.2-1.5-1-1.5a1.5 1.5 0 0 1 0-3c.8 0 1-.7 1-1.5V5h2.5C7.3 5 8 4.8 8 4Z" fill={C.leaf} />),
  mouse: (<>
    <rect x="7" y="3.5" width="10" height="17" rx="5" fill={C.metal} /><path d="M12 6v3" stroke={C.metalDk} strokeWidth={1.8} fill="none" />
  </>),
  wheat: (<>
    <path d="M12 21V6" stroke={C.stem} strokeWidth={1.8} fill="none" />
    <path d="M12 7c-1.5-1-2.5-2.6-2.5-4.5C11 3.5 12 5.1 12 7Zm0 0c1.5-1 2.5-2.6 2.5-4.5C13 3.5 12 5.1 12 7Z" fill={C.gold} />
    <path d="M12 11c-1.6-.9-2.8-2.4-3-4.3 1.7.4 2.8 2 3 4.3Zm0 0c1.6-.9 2.8-2.4 3-4.3-1.7.4-2.8 2-3 4.3Z" fill={C.gold} />
  </>),
  "droplet-small": (<path d="M12 5c2.4 3 4 5.1 4 7.3A4 4 0 0 1 8 12.3C8 10.1 9.6 8 12 5Z" fill={C.water} />),
  note: (<>
    <path d="M6 3h9l3 3v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" fill={C.paper} />
    <path d="M15 3v3h3M7 10h8M7 13h8M7 16h5" stroke={C.woodDk} strokeWidth={1} fill="none" opacity=".6" />
  </>),
  bottle: (<>
    <path d="M10 3h4v3l1.5 2c.5.7.5 1.5.5 2.3V19a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-8.4c0-.8 0-1.6.5-2.3L10 6z" fill={C.glass} />
    <rect x="9.5" y="2.5" width="5" height="1.8" rx=".6" fill={C.wood} stroke="none" /><rect x="8.5" y="13" width="7" height="3" rx=".5" fill={C.paper} stroke="none" />
  </>),

  // ── phase 3 — weather ──
  "wx-bright": (<>
    <circle cx="12" cy="12" r="4.5" fill={C.gold} />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.8 4.8 7 7M17 17l2.2 2.2M19.2 4.8 17 7M7 17l-2.2 2.2" stroke={C.gold} strokeWidth={2} fill="none" />
  </>),
  "wx-clear": (<>
    <circle cx="12" cy="12" r="4.5" fill={C.gold} />
    <path d="M12 3.5v2.5M12 18v2.5M3.5 12H6M18 12h2.5M6 6l1.7 1.7M16.3 16.3 18 18M18 6l-1.7 1.7M7.7 16.3 6 18" stroke={C.goldDk} strokeWidth={1.8} fill="none" />
  </>),
  "wx-fog": (<>
    <circle cx="12" cy="9" r="3.4" fill={C.gold} opacity=".45" />
    <path d="M3.5 9.5h6M14.5 9.5h6M2.5 13h19M4.5 16.5h15M6.5 20h11" stroke={C.stone} strokeWidth={1.9} strokeLinecap="round" fill="none" />
  </>),
  "wx-partly": (<>
    <circle cx="9" cy="9" r="3.5" fill={C.gold} />
    <path d="M9 3.5v2M4.5 9H3M13.2 5.5 12 6.7M5.8 5.5 7 6.7" stroke={C.goldDk} strokeWidth={1.6} fill="none" />
    <path d="M10 18a3.5 3.5 0 0 1-.4-7 4.5 4.5 0 0 1 8.5 1.5 3 3 0 0 1-.6 5.5z" fill={C.glass} />
  </>),
  "wx-mostly": (<>
    <circle cx="8.5" cy="8" r="3" fill={C.gold} />
    <path d="M9 17a3.8 3.8 0 0 1-.4-7.6 4.8 4.8 0 0 1 9 1.6 3.2 3.2 0 0 1-.6 6z" fill={C.stone} />
  </>),
  "wx-overcast": (<>
    <path d="M8 16a3.6 3.6 0 0 1-.4-7.2 4.6 4.6 0 0 1 8.6 1.5 3 3 0 0 1-.5 5.7z" fill={C.stone} opacity=".8" />
    <path d="M10 19a3.6 3.6 0 0 1-.4-7.2 4.6 4.6 0 0 1 8.6 1.5 3 3 0 0 1-.5 5.7z" fill={C.metalDk} />
  </>),
  "wx-lightrain": (<>
    <path d="M8 13a3.6 3.6 0 0 1-.4-7.2 4.6 4.6 0 0 1 8.6 1.5 3 3 0 0 1-.5 5.7z" fill={C.stone} />
    <path d="M8 16l-1 2.5M12 16l-1 2.5M16 16l-1 2.5" stroke={C.water} strokeWidth={1.8} fill="none" />
  </>),
  "wx-heavyrain": (<>
    <path d="M8 12a3.6 3.6 0 0 1-.4-7.2 4.6 4.6 0 0 1 8.6 1.5 3 3 0 0 1-.5 5.7z" fill={C.metalDk} />
    <path d="M7 15l-1.5 4M11 15l-1.5 4M15 15l-1.5 4M9 15.5l-1 3M13 15.5l-1 3" stroke={C.waterDk} strokeWidth={1.8} fill="none" />
  </>),
  "wx-storm": (<>
    <path d="M8 11a3.6 3.6 0 0 1-.4-7.2 4.6 4.6 0 0 1 8.6 1.5 3 3 0 0 1-.5 5.7z" fill={C.metalDk} />
    <path d="M12 12l-3 4h3l-2 4 5-5h-3l2-3z" fill={C.gold} stroke={C.goldDk} strokeWidth={1} />
  </>),

  // ── phase 3 — time of day ──
  "tod-dawn": (<>
    <path d="M3 18h18" stroke={C.metalDk} strokeWidth={1.6} fill="none" />
    <path d="M6.5 18a5.5 5.5 0 0 1 11 0z" fill={C.petalP} />
    <path d="M12 6v3M6 9l1.5 1.5M18 9l-1.5 1.5" stroke={C.gold} strokeWidth={1.6} fill="none" />
  </>),
  "tod-sunrise": (<>
    <path d="M3 18h18" stroke={C.metalDk} strokeWidth={1.6} fill="none" />
    <path d="M6.5 18a5.5 5.5 0 0 1 11 0z" fill={C.gold} />
    <path d="M12 4v3.5M4.5 11 6 12.5M19.5 11 18 12.5M2.5 18H5M19 18h2.5" stroke={C.goldDk} strokeWidth={1.6} fill="none" />
    <path d="M9 21l3-2 3 2" stroke={C.gold} strokeWidth={1.6} fill="none" />
  </>),
  "tod-day": (<>
    <circle cx="12" cy="12" r="4.5" fill={C.gold} />
    <path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.5 5.5 7.2 7.2M16.8 16.8 18.5 18.5M18.5 5.5 16.8 7.2M7.2 16.8 5.5 18.5" stroke={C.goldDk} strokeWidth={1.8} fill="none" />
  </>),
  "tod-sunset": (<>
    <path d="M3 18h18" stroke={C.metalDk} strokeWidth={1.6} fill="none" />
    <path d="M6.5 18a5.5 5.5 0 0 1 11 0z" fill={C.red} />
    <path d="M12 4v3.5M4.5 11 6 12.5M19.5 11 18 12.5" stroke={C.goldDk} strokeWidth={1.6} fill="none" />
    <path d="M9 21l3 2 3-2" stroke={C.red} strokeWidth={1.6} fill="none" />
  </>),
  "tod-dusk": (<>
    <path d="M3 18h18" stroke={C.metalDk} strokeWidth={1.6} fill="none" />
    <path d="M6.5 18a5.5 5.5 0 0 1 11 0z" fill={C.woodDk} />
    <path d="M12 5v2.5" stroke={C.gold} strokeWidth={1.6} fill="none" />
    <circle cx="16" cy="7" r="1" fill={C.gold} stroke="none" /><circle cx="7" cy="8" r=".8" fill={C.gold} stroke="none" />
  </>),
  "tod-night": (<>
    <path d="M17 14.5A6.5 6.5 0 0 1 9.5 7a5 5 0 1 0 7.5 7.5Z" fill={C.glass} />
    <circle cx="17" cy="6" r=".9" fill={C.gold} stroke="none" /><circle cx="19.5" cy="9" r=".7" fill={C.gold} stroke="none" /><circle cx="14.5" cy="4.5" r=".6" fill={C.gold} stroke="none" />
  </>),
};

export function Icon({ name, size, className, style }: { name: IconName; size?: number; className?: string; style?: CSSProperties }) {
  return <Svg size={size} className={className} style={style}>{PATHS[name]}</Svg>;
}

/** Inline gold coin for use in TEXT ("🪙 42" → `<CoinIcon /> 42`). Baseline-aligned so it sits on the line
 *  like the emoji did. Size defaults to a hair larger than the text cap-height. */
export function CoinIcon({ size = 15 }: { size?: number }) {
  return <Icon name="coin" size={size} style={{ display: "inline-block", verticalAlign: "-0.16em", margin: "0 0.05em" }} />;
}
